/**
 * Generic in-memory TTL cache with in-flight request coalescing.
 * Stateless factory — create one instance per domain (e.g. resort).
 */

const isExpired = (entry) =>
  entry == null || (entry.expiresAt != null && Date.now() >= entry.expiresAt);

export const createMemoryCache = ({
  enabled = true,
  defaultTtlMs = 5 * 60 * 1000,
} = {}) => {
  let cacheEnabled = Boolean(enabled);
  let fallbackTtlMs = Number(defaultTtlMs) > 0 ? Number(defaultTtlMs) : 0;

  const store = new Map();
  const inflight = new Map();

  const resolveEnabled = (override) =>
    override == null ? cacheEnabled : Boolean(override);

  const resolveTtl = (ttlMs) => {
    if (ttlMs == null) return fallbackTtlMs;
    const n = Number(ttlMs);
    return Number.isFinite(n) && n >= 0 ? n : fallbackTtlMs;
  };

  const get = (key) => {
    if (!cacheEnabled) return undefined;
    const entry = store.get(key);
    if (isExpired(entry)) {
      store.delete(key);
      return undefined;
    }
    return entry.value;
  };

  const set = (key, value, ttlMs) => {
    if (!cacheEnabled) return value;
    const ttl = resolveTtl(ttlMs);
    store.set(key, {
      value,
      expiresAt: ttl > 0 ? Date.now() + ttl : null,
    });
    return value;
  };

  const remove = (key) => {
    store.delete(key);
    inflight.delete(key);
  };

  const removeByPrefix = (prefix) => {
    const p = String(prefix || "");
    if (!p) return;
    for (const key of store.keys()) {
      if (key.startsWith(p)) store.delete(key);
    }
    for (const key of inflight.keys()) {
      if (key.startsWith(p)) inflight.delete(key);
    }
  };

  const clear = () => {
    store.clear();
    inflight.clear();
  };

  /**
   * Return cached value or run fetcher once (coalesced for concurrent callers).
   * Pass `enabled: false` or `ttlMs: 0` to bypass caching for a single call.
   */
  const getOrFetch = async (key, fetcher, options = {}) => {
    const useCache = resolveEnabled(options.enabled);
    const ttl = resolveTtl(options.ttlMs);

    if (!useCache || ttl === 0) {
      return fetcher();
    }

    const cached = get(key);
    if (cached !== undefined) {
      return cached;
    }

    if (inflight.has(key)) {
      return inflight.get(key);
    }

    const promise = Promise.resolve()
      .then(fetcher)
      .then((value) => {
        set(key, value, ttl);
        return value;
      })
      .finally(() => {
        inflight.delete(key);
      });

    inflight.set(key, promise);
    return promise;
  };

  return {
    get,
    set,
    remove,
    removeByPrefix,
    clear,
    getOrFetch,
    isEnabled: () => cacheEnabled,
    setEnabled: (next) => {
      cacheEnabled = Boolean(next);
      if (!cacheEnabled) clear();
    },
    getDefaultTtlMs: () => fallbackTtlMs,
    setDefaultTtlMs: (next) => {
      const n = Number(next);
      if (Number.isFinite(n) && n >= 0) fallbackTtlMs = n;
    },
    size: () => store.size,
    keys: () => Array.from(store.keys()),
  };
};
